<script setup>
import { computed, shallowRef, useTemplateRef, nextTick } from 'vue';
import { PencilIcon } from '@heroicons/vue/24/outline';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useSortable } from '@vueuse/integrations/useSortable';

const props = defineProps({
  allAssets: {
    type: Object,
    required: true
  },
  group: {
    type: Object,
    default: {id: null}
  },
});

const editName = shallowRef(false);

function toggleEditName() {
  editName.value = !editName.value;
}

const assets = computed(() => {
  return props.allAssets
    .filter(asset => asset.group_id == props.group.id);
});

const emit = defineEmits(['remove-group', 'restore-group', 'order-assets']);

const assetsRef = useTemplateRef('assetsRef');

useSortable(assetsRef, assets.value, {
  preventDeafault: true,
  onUpdate: (e) => {
    const target = assets.value[e.oldIndex];
    nextTick(() => {
      emit('order-assets', target, e.newIndex)
    })
  },
  handle: '.asset-sort-handle',
  animation: 300,
  dragClass: 'opacity-25',
  ghostClass: '!bg-blue-100',
  chosenClass: 'opacity-25',
});

</script>


<template>
<div class="relative flex flex-col">
  <FontAwesomeIcon
    v-if="group.willBeDeleted"
    :icon="['fal', 'fa-trash-xmark']"
    class="absolute inset-0 h-full text-red-200 m-auto opacity-50 z-20 pointer-events-none"
    title="Delete Group"
  />
  <div
    v-if="group.id"
    class="flex justify-between"
  >
    <FontAwesomeIcon
      :icon="['fas', 'sort']"
      class="inline h-5 mb-2 cursor-grab sort-handle text-gray-500 hover:text-gray-900"
      title="Drag to reorder"
    />
    <FontAwesomeIcon
      v-if="!group.willBeDeleted"
      @click="emit('remove-group', group)"
      :icon="['fal', 'fa-trash-xmark']"
      class="inline h-5 cursor-pointer text-red-500 hover:text-red-700"
      title="Delete Group"
    />
    <FontAwesomeIcon
      v-if="group.willBeDeleted"
      @click="emit('restore-group', group)"
      :icon="['fas', 'fa-trash-undo']"
      class="inline h-5 cursor-pointer text-blue-600 hover:text-blue-600"
      title="Restore Group"
    />
  </div>
  <div v-if="group.id">
    <span v-if="!editName">
      <FontAwesomeIcon
        @click="toggleEditName"
        :icon="['fas', 'fa-pencil']"
        class="inline h-4 cursor-pointer text-gray-500 hover:text-gray-900"
        title="Edit Group Name"
      />
      {{ group.name }}
    </span>

    <input
      v-if="group.created_at || !group.willBeDeleted"
      @keyup.enter.stop.prevent="toggleEditName"
      :type="editName ? 'text' : 'hidden'"
      :name="group.created_at
        ? `existingMediaGroups[${group.id}][name]`
        : `newMediaGroups[${group.id}][name]`
      "
      v-model="group.name"
      placeholder="Group Name"
      class="w-full p-2 border border-gray-300 rounded"
    />
  </div>

  <div
    ref="assetsRef"
    class="p-1 min-h-12 flex flex-wrap gap-2"
  >
    <slot :assets="assets"></slot>
  </div>


  <div v-if="group.id">
    <input
      v-if="group.created_at"
      type="hidden"
      :name="`existingMediaGroups[${group.id}][id]`"
      :value="group.id"
    />
    <input
      v-if="group.created_at || !group.willBeDeleted"
      type="hidden"
      :name="group.created_at
        ? `existingMediaGroups[${group.id}][order]`
        : `newMediaGroups[${group.id}][order]`
        "
      :value="group.order"
    />
  </div>

</div>
</template>


